import {chain} from 'lodash';
import Token from 'markdown-it/lib/token';
import React, {memo} from 'react';

import {
  buildInlineMarkdownTokens,
  buildMarkdownTokens,
  isCommonMarkGap,
} from '../../../src/services/markdown';
import {NodeTransform} from './markdownFormatterTypes';

/*
 * Props.
 */

interface MarkdownPlainFormatterProps {
  text: string;
  transform?: NodeTransform;
  isInline?: boolean;
}

/*
 * Component.
 */

export const MarkdownPlainFormatter = memo<MarkdownPlainFormatterProps>((props) => {
  // Build the markdown tokens from the provided text.
  const {text, transform} = props;
  const tokens = props.isInline ? buildInlineMarkdownTokens(text) : buildMarkdownTokens(text);

  // If no transform was provided, return.
  if (!transform) {
    const stringifiedTokens = stringifyTokens(tokens).trim();
    return <>{stringifiedTokens}</>;
  }

  // Otherwise, apply the tranform.
  return <>{runTransforms(tokens, transform)}</>;
});

/*
 * Markdown logic.
 */

function runTransforms(tokens: ReadonlyArray<Token>, transform: NodeTransform) {
  const transformed = chain(tokens)
    .filter((t) => !t.hidden)
    .map((token, index) => {
      const stringifiedToken = stringifyToken(token);
      return (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index.toString()}>
          {token.type !== 'fence' ? transform(stringifiedToken) : stringifiedToken}
        </React.Fragment>
      );
    })
    .value();
  return transformed;
}

function stringifyTokens(tokens: ReadonlyArray<Token>) {
  return chain(tokens)
    .filter((t) => !t.hidden)
    .map(stringifyToken)
    .join('')
    .value();
}

function stringifyToken(token: Token): string {
  switch (token.type) {
    case 'inline':
      return stringifyTokens(token.children);

    case 'softbreak':
    case 'blockquote_open':
    case 'list_item_open':
    case 'paragraph_open':
      return ' ';

    case 'fence':
      return token.markup + token.content.trim() + token.markup;

    case 'image':
      return token.content || token.attrGet('src') || ' ';

    case 'html_inline':
    case 'html_block':
      if (isCommonMarkGap(token.content)) {
        return '';
      }

      return token.content;

    default:
      return token.content;
  }
}
